import type { Attachment } from 'svelte/attachments';

const ACTIVITY_EVENTS = [
	'click',
	'touchstart',
	'touchmove',
	'keydown',
	'scroll',
	'mousemove',
	'mousedown',
	'pointerdown',
	'pointermove'
] as const;

/**
 * Creates an inactivity timer attachment that monitors user activity
 * and triggers a callback after a specified period of inactivity.
 *
 * @param timeoutMs - Milliseconds of inactivity before triggering callback
 * @param onTimeout - Function to call when timeout is reached
 * @param enabled - Whether the timer is active (defaults to true)
 * @returns Attachment function for use with {@attach ...}
 */
export function inactivityTimer(
	timeoutMs: number,
	onTimeout: () => void,
	enabled: boolean = true
): Attachment {
	return (element: Element) => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let debounceId: ReturnType<typeof setTimeout> | null = null;

		const resetTimer = () => {
			if (!enabled) {
				return;
			}

			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(onTimeout, timeoutMs);
		};

		const debouncedResetTimer = () => {
			if (!enabled) {
				return;
			}

			if (debounceId) {
				clearTimeout(debounceId);
			}
			debounceId = setTimeout(resetTimer, 100);
		};

		const listeners: Array<() => void> = [];

		if (enabled) {
			for (const eventType of ACTIVITY_EVENTS) {
				const listener = debouncedResetTimer;
				element.addEventListener(eventType, listener, { passive: true });
				listeners.push(() => element.removeEventListener(eventType, listener));
			}

			resetTimer();
		}

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			if (debounceId) {
				clearTimeout(debounceId);
			}
			listeners.forEach((cleanup) => cleanup());
		};
	};
}
